import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  TrendingUp,
  Plug,
  Upload,
  FileText,
  Settings,
  User,
  ChevronRight,
  ChevronDown,
  Crown,
  CreditCard,
  LogOut,
  Building2,
  Users,
  ArrowUpDown,
  Package,
  Code2,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useTranslation } from "@/contexts/LanguageContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { UserProfileMenu } from "@/components/UserProfileMenu";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/hooks/useAuth";

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const { t } = useTranslation();
  const { isSuperAdmin } = useUserProfile();
  const { signOut } = useAuth();
  const isCollapsed = state === "collapsed";
  
  // Estados para controlar grupos colapsáveis (vertical collapse)
  const [isPrincipalOpen, setIsPrincipalOpen] = useState(true);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);

  const mainItems = [
    { title: t('dashboard'), url: "/dashboard", icon: LayoutDashboard },
    { title: t('customersMenu'), url: "/customers-saas", icon: Users },
    { title: "Produtos", url: "/products", icon: Package },
    { title: t('transactionsMenu'), url: "/transactions", icon: ArrowUpDown },
    { title: t('metrics'), url: "/metrics", icon: TrendingUp },
    { title: t('integrations'), url: "/integrations", icon: Plug },
    { title: t('import'), url: "/import", icon: Upload },
    { title: t('reports'), url: "/reports", icon: FileText },
  ];

  const accountItems = [
    { title: t('settings'), url: "/settings", icon: Settings },
    { title: t('account'), url: "/account", icon: User },
  ];

  const superAdminItems = isSuperAdmin ? [
    { title: t('plans'), url: "/plans", icon: Crown },
    { title: "Gateway Pagamento", url: "/payment-gateway", icon: CreditCard },
    { title: "Gerenciar Tenants", url: "/admin/tenants", icon: Building2 },
    { title: "Scripts", url: "/admin/head-scripts", icon: Code2 },
  ] : [];

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-border bg-background">
      {/* Header */}
      <SidebarHeader className="border-b border-border p-4 bg-background">
        {!isCollapsed ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary gradient-primary">
                <TrendingUp className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-display text-primary">Dashi</span>
                <span className="text-xs font-regular text-muted-foreground">Dashboard SaaS</span>
              </div>
            </div>
            <SidebarTrigger className="h-8 w-8" />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary gradient-primary">
              <TrendingUp className="h-5 w-5 text-primary-foreground" />
            </div>
            <SidebarTrigger className="h-8 w-8" />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="bg-background">
        {/* Main Navigation - Collapsible Group */}
        <Collapsible open={isCollapsed ? true : isPrincipalOpen} onOpenChange={setIsPrincipalOpen}>
          <SidebarGroup>
            {!isCollapsed && (
              <CollapsibleTrigger className="w-full">
                <SidebarGroupLabel className="text-xs font-medium uppercase tracking-wider text-muted-foreground px-2 flex items-center justify-between cursor-pointer hover:text-foreground transition-colors">
                  <span>Principal</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isPrincipalOpen ? '' : '-rotate-90'}`} />
                </SidebarGroupLabel>
              </CollapsibleTrigger>
            )}
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {mainItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive(item.url)}
                        tooltip={isCollapsed ? item.title : undefined}
                      >
                        <NavLink to={item.url} className="flex items-center gap-3">
                          <item.icon className="h-5 w-5" />
                          {!isCollapsed && <span>{item.title}</span>}
                          {!isCollapsed && isActive(item.url) && (
                            <ChevronRight className="ml-auto h-4 w-4" />
                          )}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Separator className="my-2" />

        {/* Super Admin Section - Collapsible Group */}
        {isSuperAdmin && (
          <>
            <Collapsible open={isCollapsed ? true : isAdminOpen} onOpenChange={setIsAdminOpen}>
              <SidebarGroup>
                {!isCollapsed && (
                  <CollapsibleTrigger className="w-full">
                    <SidebarGroupLabel className="text-xs font-medium uppercase tracking-wider text-muted-foreground px-2 flex items-center justify-between cursor-pointer hover:text-foreground transition-colors">
                      <span>Administração</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isAdminOpen ? '' : '-rotate-90'}`} />
                    </SidebarGroupLabel>
                  </CollapsibleTrigger>
                )}
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {superAdminItems.map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton
                            asChild
                            isActive={isActive(item.url)}
                            tooltip={isCollapsed ? item.title : undefined}
                          >
                            <NavLink to={item.url} className="flex items-center gap-3">
                              <item.icon className="h-5 w-5" />
                              {!isCollapsed && <span>{item.title}</span>}
                              {!isCollapsed && isActive(item.url) && (
                                <ChevronRight className="ml-auto h-4 w-4" />
                              )}
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
            <Separator className="my-2" />
          </>
        )}

        {/* Account Section - Collapsible Group */}
        <Collapsible open={isCollapsed ? true : isAccountOpen} onOpenChange={setIsAccountOpen}>
          <SidebarGroup>
            {!isCollapsed && (
              <CollapsibleTrigger className="w-full">
                <SidebarGroupLabel className="text-xs font-medium uppercase tracking-wider text-muted-foreground px-2 flex items-center justify-between cursor-pointer hover:text-foreground transition-colors">
                  <span>Conta</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isAccountOpen ? '' : '-rotate-90'}`} />
                </SidebarGroupLabel>
              </CollapsibleTrigger>
            )}
            <CollapsibleContent>
              <SidebarGroupContent>
                {/* User Profile */}
                {!isCollapsed ? (
                  <div className="px-2 mb-3">
                    <UserProfileMenu collapsed={isCollapsed} />
                  </div>
                ) : (
                  <div className="flex justify-center mb-2">
                    <UserProfileMenu collapsed={isCollapsed} />
                  </div>
                )}
                
                {/* Account Menu Items */}
                <SidebarMenu>
                  {/* Notifications */}
                  <NotificationBell collapsed={isCollapsed} />
                  {accountItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive(item.url)}
                        tooltip={isCollapsed ? item.title : undefined}
                      >
                        <NavLink to={item.url} className="flex items-center gap-3">
                          <item.icon className="h-5 w-5" />
                          {!isCollapsed && <span>{item.title}</span>}
                          {!isCollapsed && isActive(item.url) && (
                            <ChevronRight className="ml-auto h-4 w-4" />
                          )}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="border-t border-border p-2 bg-background">
        {/* Logout Button */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={signOut}
              tooltip={isCollapsed ? "Sair" : undefined}
              className="w-full text-destructive hover:bg-destructive/10"
            >
              <LogOut className="h-5 w-5" />
              {!isCollapsed && <span>Sair</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
